<template>
    <main>
        <section
            id="test"
            class="transition overflow-hidden"
            :style="{'--fade-up-direction': fadeUpDirection}"
            :class="!result ? 'bg-secondary' : 'bg-primary text-white'"
        >
            <b-container>
                <b-row class="min-vh-100">
                    <b-col
                        cols="12"
                        lg="8"
                        xxl="7"
                        class="d-flex flex-column align-items-start py-4 py-lg-5 pr-lg-5"
                    >
                        <navbar
                            class="mb-4 mb-lg-5"
                            no-menu
                            :white-logo="!!result"
                        />

                        <b-progress
                            :value="progress"
                            :max="questions.length"
                            style="width: 11.5rem;"
                        />

                        <transition
                            name="fade-up"
                            mode="out-in"
                        >
                            <question
                                v-if="currentQuestion && !result"
                                :key="currentQuestion.id"
                                class="my-auto"
                                :question="currentQuestion"
                                :show-back="!!history[0]"
                                @answered="showNext"
                                @back="goBack"
                            />

                            <div v-else-if="result">
                                <div class="fade-up">
                                    <result
                                        :result="result"
                                        class="mb-4 mb-lg-5"
                                    />

                                    <b-btn
                                        size="sm"
                                        variant="outline-light"
                                        class="btn-xs-down-block"
                                        @click="goBack"
                                    >
                                        {{ $t('common.buttons.back') }}
                                    </b-btn>

                                    <b-btn
                                        size="sm"
                                        variant="light"
                                        class="btn-xs-down-block mx-md-2"
                                        @click="resetData"
                                    >
                                        {{ $t('common.buttons.redoTest') }}
                                    </b-btn>

                                    <b-btn
                                        size="sm"
                                        variant="outline-light"
                                        class="btn-xs-down-block"
                                        :to="{name: `home${routePrefix}`}"
                                    >
                                        {{ $t('common.buttons.goToHome') }}
                                    </b-btn>
                                </div>
                            </div>
                        </transition>
                    </b-col>

                    <!-- Story -->
                    <transition
                        name="fade-left"
                        mode="out-in"
                    >
                        <b-col
                            v-if="result && story"
                            key="story"
                            class="right-col py-4 pt-lg-10 pl-lg-5"
                        >
                            <p
                                v-if="linkToDifferentTest"
                                class="position-relative mb-3 mb-xl-4"
                            >
                                <i18next :path="linkToDifferentTest.path">
                                    {{story.author.name}}
                                    <b-link
                                        :to="linkToDifferentTest.to"
                                        class="font-weight-bold text-white"
                                        v-text="linkToDifferentTest.text"
                                    />
                                </i18next>
                            </p>

                            <p
                                v-else
                                class="position-relative mb-4 mb-xl-5">
                                {{ $t('stories.linkStory', {author: story.author.name}) }}
                            </p>

                            <b-row
                                align-h="center"
                                align-v="center"
                                class="justify-content-lg-between"
                            >
                                <b-col
                                    cols="6"
                                    sm="4"
                                    lg="8"
                                    xl="5"
                                    class="mb-2 mb-xl-0"
                                >
                                    <story-author
                                        :author="story.author"
                                        class="author-sm"
                                    />
                                </b-col>

                                <b-col
                                    cols="8"
                                    sm="5"
                                    lg="12"
                                    xl="7"
                                    class="text-center text-sm-left"
                                >
                                    <story-link :story="story" />
                                </b-col>
                            </b-row>
                        </b-col>

                        <!-- Button close. -->
                        <b-col
                            v-if="!result"
                            key="button-close"
                            class="d-none d-lg-block py-lg-5 text-right"
                        >
                            <b-link
                                class="small text-primary text-decoration-none"
                                :to="{name: `home${routePrefix}`}"
                            >
                                {{ $t('common.buttons.closeTest') }}

                                <b-btn
                                    class="btn-circle ml-2"
                                    size="sm"
                                    variant="outline-primary"
                                >
                                    <fa icon="times" />
                                </b-btn>
                            </b-link>
                        </b-col>
                    </transition>
                </b-row>
            </b-container>
        </section>
    </main>
</template>

<script>
import resultsGeneral from '@/contents/results.json';
import resultsCancer from '@/contents/resultsCancer.json';
import stories from '@/contents/stories.json';
import questions from '@/contents/questions';
import Result from '@/components/test/Result';
import Navbar from '@/components/common/Navbar';
import StoryLink from '@/components/common/StoryLink';
import Question from '@/components/test/question/Index';
import StoryAuthor from '@/components/common/StoryAuthor';

export default {
    name: 'Test',
    components: { Question, Result, StoryAuthor, StoryLink, Navbar },
    data() {
        return {
            questions,
            results: null,
            stories,
            currentQuestion: questions[0],
            result: null,
            story: null,
            progress: 0,
            history: [],
            fadeUpDirection: 1,
            routePrefix: undefined,
        };
    },
    computed: {
        linkToDifferentTest() {
            if (!this.story) return null;

            const otherType = this.routePrefix === 'Cancer' ? 'General' : 'Cancer';

            if (this.story.type === otherType.toLowerCase()) {
                return {
                    path: `stories.linkToTest${otherType}`,
                    to: {name: `home${otherType === 'General' ? '' : 'Cancer'}`},
                    text: this.$t(`stories.test${otherType}`),
                };
            }

            return null;
        },
    },
    watch: {
        $route: {
            handler: function(val) {
                this.routePrefix = val.name.endsWith('Cancer') ? 'Cancer' : '';
                this.results = val.name === 'testCancer' ? resultsCancer : resultsGeneral;
            },
            immediate: true,
        },
    },
    methods: {
        showNext(target) {
            // Return fade up direction to normal.
            this.fadeUpDirection = 1;

            // Save current question id to enable going back.
            this.history.push(this.currentQuestion.id);

            if (target.nextId) {
                this.progress++;

                // Go to next question.
                this.currentQuestion = this.questions.find(q => q.id === target.nextId);
            } else if (target.resultId) {
                this.progress = this.questions.length;

                this.result = this.results.find(r => r.id === target.resultId);

                if (this.result.storyId) {
                    this.story = this.stories.find(s => s.id === this.result.storyId);
                }
            }
        },
        resetData() {
            this.currentQuestion = this.questions[0];

            this.progress = 0;

            this.result = null;

            this.story = null;

            this.history = [];
        },
        goBack() {
            let target = this.history.pop();

            if (this.result) {
                this.result = null;
            }

            // Reverse fade up direction.
            this.fadeUpDirection = -1;

            // Go back to the previous question.
            this.currentQuestion = this.questions.find(q => q.id === target);
        },
    },
};
</script>

<style lang="scss" scoped>
.right-col::before {
    position: absolute;
    top: 0;
    right: -50vw;
    bottom: 0;
    left: 0;
    content: '';
    background-color: $info;

    @include media-breakpoint-down(md) {
        left: -20vw;
    }
}
</style>
